/**
 * SHEEP MADE SIMPLE
 * Spring Plan 2026
 * Version 1.1.0 - reusable definition asset
 */
(() => {
  "use strict";

  const SEASON = "spring";
  const YEAR = 2026;
  function init() {
    const root = document.getElementById("sms-spring-plan");
    if (!root || root.dataset.ready === "1") return;
    if (
      !window.SMSFarmStorage ||
      !window.SMSSeasonalTaskMetadata ||
      !window.SMSSeasonalEngine ||
      !window.SMSSeasonalEngine.getDefinition(SEASON) ||
      !window.SMSSeasonalSummary
    ) {
      setTimeout(init, 80);
      return;
    }

    const TASK_ORDER = window.SMSSeasonalEngine.getDefinition(SEASON).taskOrder;
    root.dataset.ready = "1";
    document.body.classList.add("sms-member-page");

    const menu = root.querySelector("[data-spring-menu]");
    if (menu) {
      menu.addEventListener("click", () => {
        const open = root.classList.toggle("is-menu-open");
        menu.setAttribute("aria-expanded", open ? "true" : "false");
      });
    }

    const dateElement = root.querySelector("[data-current-date]");
    if (dateElement) {
      dateElement.textContent = new Intl.DateTimeFormat("en-NZ", {
        weekday: "short",
        day: "numeric",
        month: "long",
        year: "numeric"
      }).format(new Date());
    }

    const toast = root.querySelector("[data-save-toast]");
    let toastTimer = null;
    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add("is-visible");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 2200);
    }

    function selectedValue(taskId, name) {
      const checked = root.querySelector(`[data-task-form="${taskId}"] input[name="${name}"]:checked`);
      return checked ? checked.value : null;
    }

    function checkedValues(taskId, name) {
      return Array.from(root.querySelectorAll(`[data-task-form="${taskId}"] input[name="${name}"]:checked`)).map(input => input.value);
    }

    function numberValue(selector) {
      const field = root.querySelector(selector);
      if (!field || field.value === "") return null;
      const number = Number(field.value);
      return Number.isFinite(number) ? number : null;
    }

    function fieldValue(selector) {
      const field = root.querySelector(selector);
      return field && field.value ? field.value : null;
    }

    function collectTaskData(taskId) {
      if (taskId === "lambingManagement") {
        return {
          lambingStatus: selectedValue(taskId, "lambingStatus"),
          dailyChecks: selectedValue(taskId, "dailyChecks")
        };
      }
      if (taskId === "eweAndLambSupport") {
        return {
          eweCondition: selectedValue(taskId, "eweCondition"),
          supplementation: selectedValue(taskId, "supplementation")
        };
      }
      if (taskId === "grazingRotation") {
        return {
          currentRotation: selectedValue(taskId, "currentRotation"),
          grassStatus: selectedValue(taskId, "grassStatus"),
          growthStrategies: checkedValues(taskId, "growthStrategies")
        };
      }
      if (taskId === "springHealth") {
        return {
          wormRisk: selectedValue(taskId, "wormRisk"),
          feetStatus: selectedValue(taskId, "feetStatus"),
          lambGrowth: selectedValue(taskId, "lambGrowth")
        };
      }
      if (taskId === "lambProcedures") {
        return {
          status: selectedValue(taskId, "procedureStatus"),
          plannedDate: fieldValue("#sp-planned-date"),
          plannedLambCount: numberValue("#sp-planned-count"),
          completedDate: fieldValue("#sp-completed-date"),
          completedLambCount: numberValue("#sp-completed-count"),
          procedures: checkedValues(taskId, "procedures")
        };
      }
      if (taskId === "shearingAndFlystrike") {
        return {
          shearingStatus: selectedValue(taskId, "shearingStatus"),
          shearingDate: fieldValue("#sp-shearing-date"),
          flystrikeMonitoring: selectedValue(taskId, "flystrikeMonitoring")
        };
      }
      return {};
    }

    function setRadio(taskId, name, value) {
      root.querySelectorAll(`[data-task-form="${taskId}"] input[name="${name}"]`).forEach(input => {
        input.checked = String(input.value) === String(value ?? "");
      });
    }

    function setChecks(taskId, name, values) {
      const set = new Set(Array.isArray(values) ? values : []);
      root.querySelectorAll(`[data-task-form="${taskId}"] input[name="${name}"]`).forEach(input => {
        input.checked = set.has(input.value);
      });
    }

    function setField(selector, value) {
      const field = root.querySelector(selector);
      if (field) field.value = value ?? "";
    }

    function restoreTask(taskId, task) {
      if (!task?.data) return;
      const data = task.data;
      if (taskId === "lambingManagement") {
        setRadio(taskId, "lambingStatus", data.lambingStatus);
        setRadio(taskId, "dailyChecks", data.dailyChecks);
      } else if (taskId === "eweAndLambSupport") {
        setRadio(taskId, "eweCondition", data.eweCondition);
        setRadio(taskId, "supplementation", data.supplementation);
      } else if (taskId === "grazingRotation") {
        setRadio(taskId, "currentRotation", data.currentRotation);
        setRadio(taskId, "grassStatus", data.grassStatus);
        setChecks(taskId, "growthStrategies", data.growthStrategies);
      } else if (taskId === "springHealth") {
        setRadio(taskId, "wormRisk", data.wormRisk);
        setRadio(taskId, "feetStatus", data.feetStatus);
        setRadio(taskId, "lambGrowth", data.lambGrowth);
      } else if (taskId === "lambProcedures") {
        setRadio(taskId, "procedureStatus", data.status);
        setField("#sp-planned-date", data.plannedDate);
        setField("#sp-planned-count", data.plannedLambCount);
        setField("#sp-completed-date", data.completedDate);
        setField("#sp-completed-count", data.completedLambCount);
        setChecks(taskId, "procedures", data.procedures);
      } else if (taskId === "shearingAndFlystrike") {
        setRadio(taskId, "shearingStatus", data.shearingStatus);
        setField("#sp-shearing-date", data.shearingDate);
        setRadio(taskId, "flystrikeMonitoring", data.flystrikeMonitoring);
      }
      updateConditionalFields();
      updateOptionStyles();
    }

    function updateOptionStyles() {
      root.querySelectorAll(".sms-sp__option").forEach(label => {
        const input = label.querySelector("input");
        label.classList.toggle("is-selected", Boolean(input?.checked));
      });
    }

    function updateConditionalFields() {
      const lambingStatus = selectedValue("lambingManagement", "lambingStatus");
      const dailyGroup = root.querySelector("[data-daily-checks-group]");
      if (dailyGroup) {
        const active = lambingStatus === "currently_lambing";
        dailyGroup.hidden = !active;
        dailyGroup.querySelectorAll("input").forEach(input => { input.disabled = !active; });
      }

      const procedureStatus = selectedValue("lambProcedures", "procedureStatus");
      const planned = root.querySelector("[data-procedure-planned]");
      const completed = root.querySelector("[data-procedure-completed]");
      const methods = root.querySelector("[data-procedure-methods]");
      if (planned) planned.hidden = procedureStatus !== "planned";
      if (completed) completed.hidden = procedureStatus !== "completed";
      if (methods) methods.hidden = procedureStatus === "not_required" || !procedureStatus;

      const shearingStatus = selectedValue("shearingAndFlystrike", "shearingStatus");
      const shearingDateWrap = root.querySelector("[data-shearing-date-wrap]");
      if (shearingDateWrap) shearingDateWrap.hidden = !["booked", "completed"].includes(shearingStatus);
    }

    root.addEventListener("change", event => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.name === "growthStrategies") {
        const form = input.closest('[data-task-form="grazingRotation"]');
        if (form) {
          if (input.value === "continue_current_system" && input.checked) {
            form.querySelectorAll('input[name="growthStrategies"]:not([value="continue_current_system"])').forEach(other => { other.checked = false; });
          } else if (input.checked) {
            const current = form.querySelector('input[name="growthStrategies"][value="continue_current_system"]');
            if (current) current.checked = false;
          }
        }
      }

      updateConditionalFields();
      updateOptionStyles();
    });

    async function renderPlan() {
      const planResult = await window.SMSSeasonalEngine.getPlan({ season: SEASON, year: YEAR, force: true });
      if (!planResult.success) return;
      const plan = planResult.data;
      TASK_ORDER.forEach(taskId => restoreTask(taskId, plan.tasks?.[taskId]));

      const summaryResult = await window.SMSSeasonalEngine.getPlanSummary({ season: SEASON, year: YEAR, plan });
      if (!summaryResult.success) return;
      const summary = summaryResult.data;

      const progressText = root.querySelector("[data-progress-text]");
      const progressBar = root.querySelector("[data-progress-bar]");
      const savedCount = root.querySelector("[data-saved-count]");
      const updated = root.querySelector("[data-last-updated]");
      if (progressText) progressText.textContent = `${summary.percentage}% complete`;
      if (progressBar) progressBar.style.width = `${summary.percentage}%`;
      if (savedCount) savedCount.textContent = `${summary.completedTasks} of ${summary.totalTasks} decisions saved to My Block`;
      if (updated) updated.textContent = summary.updatedLabel;

      TASK_ORDER.forEach(taskId => {
        const task = plan.tasks?.[taskId];
        const details = root.querySelector(`[data-task="${taskId}"]`);
        if (!details) return;
        const saved = Boolean(task?.meta?.completed);
        details.classList.toggle("is-saved", saved);
        const state = details.querySelector("[data-task-state-label]");
        const stateSub = details.querySelector("[data-task-state-sub]");
        const buttonLabel = details.querySelector("[data-task-button-label]");
        if (state) state.textContent = saved ? "Saved" : "Not started";
        if (stateSub) stateSub.textContent = saved ? window.SMSSeasonalSummary.formatUpdatedLabel(task.meta.updatedAt) : "Make this decision to save to My Block";
        if (buttonLabel) buttonLabel.textContent = saved ? "Review →" : "Start →";
      });

      const summaryGrid = root.querySelector("[data-summary-grid]");
      if (summaryGrid) {
        summaryGrid.innerHTML = summary.tasks.map(item => {
          const complete = ["completed", "not_applicable", "review_due", "stale"].includes(item.status);
          const lines = item.secondary.map(line => `<span>${escapeHtml(line)}</span>`).join("");
          return `<article class="sms-sp__summary-card${complete ? " is-complete" : ""}"><span class="sms-sp__summary-check">✓</span><div><small>${escapeHtml(item.title)}</small><strong>${escapeHtml(item.primary)}</strong>${lines}${item.updatedLabel ? `<span>${escapeHtml(item.updatedLabel)}</span>` : ""}</div></article>`;
        }).join("");
      }
    }

    function escapeHtml(value) {
      return String(value ?? "").replace(/[&<>\"]/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[char]));
    }

    root.querySelectorAll("[data-save-task]").forEach(button => {
      button.addEventListener("click", async () => {
        const taskId = button.dataset.saveTask;
        const errorBox = root.querySelector(`[data-task-error="${taskId}"]`);
        if (errorBox) {
          errorBox.classList.remove("is-visible");
          errorBox.textContent = "";
        }
        button.classList.add("is-saving");
        button.disabled = true;
        button.textContent = "Saving…";

        const result = await window.SMSSeasonalEngine.saveTaskSnapshot({
          season: SEASON,
          year: YEAR,
          taskId,
          data: collectTaskData(taskId),
          sourceKey: `spring-plan-${YEAR}`
        });

        button.classList.remove("is-saving");
        button.disabled = false;
        if (!result.success) {
          button.textContent = "Save to My Block";
          if (errorBox) {
            errorBox.textContent = (result.errors || ["We couldn't save your changes. Please try again."]).join(" ");
            errorBox.classList.add("is-visible");
          }
          return;
        }

        button.classList.add("is-saved");
        button.textContent = "Saved to My Block ✓";
        showToast("Spring decision saved to My Block");
        await renderPlan();
      });
    });

    document.addEventListener("sms:seasonal-task-saved", event => {
      if (event.detail?.season === SEASON && event.detail?.year === YEAR) renderPlan();
    });

    updateConditionalFields();
    updateOptionStyles();
    renderPlan();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
